import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "layout__wrapper"
}
const _hoisted_2 = { class: "subscription-box" }
const _hoisted_3 = {
  key: 1,
  class: "layout__wrapper"
}
const _hoisted_4 = { class: "subscription-box" }
const _hoisted_5 = {
  key: 2,
  class: "layout__wrapper container"
}
const _hoisted_6 = ["href"]
const _hoisted_7 = {
  key: 3,
  class: "layout__wrapper container"
}
const _hoisted_8 = ["href"]

import { computed, onBeforeMount, Raw, ref, watch } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { useRouter } from 'vue-router'

import Layout from '@/app/components/Layout.vue' // @ is an alias to /src
import HeaderRoot from '@/app/components/header/Root.vue'
import BillingData from '@/app/components/billing/BillingData.vue'
import BillingDataPaid from '@/app/components/billing/BillingDataPaid.vue'
import BillingDataPaidAppStore from '@/app/components/billing/BillingDataPaidAppStore.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import ContentWrapper from '@/components/ui/ContentWrapper.vue'

import { assertNotNull } from '@/helpers/typing'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import {
  BillingView,
  createBilling,
  isAppleWeb,
  isGoogleWeb,
} from '@/models/billing'
import { iosExternalLinkPayment16218Variant } from '@/services/ab'


export default /*@__PURE__*/_defineComponent({
  __name: 'Billing',
  setup(__props) {

const router = useRouter()
const wait = useWait()

const billing = ref<Raw<BillingView> | null>(null)
const redirectDelay = ref(6000)

const isAppleWebBilling = ref(false)
const isGoogleWebBilling = ref(false)

onBeforeMount(async () => {
  wait.start('Loading Billing')
  await refreshBilling()
})

async function refreshBilling(): Promise<void> {
  wait.start('Loading Billing')
  wait.end('Error Loading Billing')
  try {
    const response = await backend.getBilling()
    billing.value = await createBilling(response)
    isAppleWebBilling.value = await isAppleWeb(response)
    isGoogleWebBilling.value = await isGoogleWeb(response)
  } catch (error) {
    wait.start('Error Loading Billing')
    // The error here is usually NetworkError or Timeout, but
    // we still capture all errors to Sentry to be aware if
    // error rate increases.
    Sentry.withScope(function (scope) {
      scope.setFingerprint(['billing-loading-error'])
      Sentry.captureException(error)
    })
  } finally {
    wait.end('Loading Billing')
  }
}

/**
 * Handles the redirect after subscription has been made.
 */
watch(
  () => billing.value?.subscribedAlertAndRedirect,
  async (value?: boolean /* , old: boolean */) => {
    if (value !== true) {
      return
    }

    // Redirect to 'books' page after delay when switching to the paid plan.
    setTimeout(() => {
      const currentRoute = router.currentRoute.value
      if (currentRoute.name !== 'billing') {
        // The user has already navigated out of the billing page,
        // skip the redirect.
        return
      }
      router.push({ name: 'books' })
    }, redirectDelay.value)
  },
)

function isLoadingBilling(): boolean {
  return wait.is('Loading Billing')
}

function isErrorLoadingBilling(): boolean {
  return wait.is('Error Loading Billing')
}

const isFree = computed(() => {
  const billingValue = assertNotNull(billing.value)
  return !billingValue.isPaidPlan
})

const isStripePaid = computed(() => {
  const billingValue = assertNotNull(billing.value)

  const isAppleExternalBilling16218 =
    billingValue.billing_type === 'stripe' &&
    iosExternalLinkPayment16218Variant() === 'B'

  return (
    billingValue.isPaidPlan &&
    (!billingValue.isAppStore() || isAppleExternalBilling16218)
  )
})

const isApplePaid = computed(() => {
  const billingValue = assertNotNull(billing.value)
  return billingValue.isPaidPlan && billingValue.isAppStore()
})

return (_ctx: any,_cache: any) => {
  const _component_LoadingError = _resolveComponent("LoadingError")!

  return (_openBlock(), _createBlock(Layout, { class: "billing-layout" }, {
    default: _withCtx(() => [
      _createVNode(HeaderRoot, { title: "Billing" }),
      (isLoadingBilling())
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("section", _hoisted_2, [
              _createVNode(LoadingSpinner)
            ])
          ]))
        : (isErrorLoadingBilling() || !billing.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("section", _hoisted_4, [
                _createVNode(_component_LoadingError)
              ])
            ]))
          : (isAppleWebBilling.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createVNode(ContentWrapper, null, {
                  default: _withCtx(() => [
                    _cache[0] || (_cache[0] = _createTextVNode(" You are subscribed with Apple in-app purchase subscription. Please use native iOS app or ", -1)),
                    _createElementVNode("a", {
                      href: billing.value.paymentProviderSettingsUrl,
                      target: "_blank",
                      class: "provider-settings-link"
                    }, " Apple subscription interface ", 8, _hoisted_6),
                    _cache[1] || (_cache[1] = _createTextVNode(" to manage your subscription. ", -1))
                  ]),
                  _: 1
                })
              ]))
            : (isGoogleWebBilling.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  _createVNode(ContentWrapper, null, {
                    default: _withCtx(() => [
                      _cache[2] || (_cache[2] = _createTextVNode(" You are subscribed through Google Play. Please use the native Android app or the ", -1)),
                      _createElementVNode("a", {
                        href: billing.value.paymentProviderSettingsUrl,
                        target: "_blank",
                        class: "provider-settings-link"
                      }, " Google Play settings ", 8, _hoisted_8),
                      _cache[3] || (_cache[3] = _createTextVNode(" to manage your subscription. ", -1))
                    ]),
                    _: 1
                  })
                ]))
              : (isFree.value)
                ? (_openBlock(), _createBlock(BillingData, {
                    key: 4,
                    billing: billing.value
                  }, null, 8, ["billing"]))
                : (isStripePaid.value)
                  ? (_openBlock(), _createBlock(BillingDataPaid, {
                      key: 5,
                      billing: billing.value
                    }, null, 8, ["billing"]))
                  : (isApplePaid.value)
                    ? (_openBlock(), _createBlock(BillingDataPaidAppStore, {
                        key: 6,
                        billing: billing.value
                      }, null, 8, ["billing"]))
                    : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})